import { useEffect, useRef } from 'react';

import { useDesktopFullscreen } from './desktop-fullscreen-context';

export interface UseDesktopSwitcherKeyboardOptions {
  enabled: boolean;
}

export const useDesktopSwitcherKeyboard = ({ enabled }: UseDesktopSwitcherKeyboardOptions) => {
  const { isFullscreen, canSwitch, hasMachineControl, switchToNext, switchToPrevious, viewerRef } =
    useDesktopFullscreen();

  const handlersRef = useRef({ switchToNext, switchToPrevious });
  handlersRef.current = { switchToNext, switchToPrevious };

  useEffect(() => {
    if (!enabled || !isFullscreen || !canSwitch) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const isDesktopSwitchShortcut =
        (event.ctrlKey || event.metaKey) &&
        (event.key === 'ArrowLeft' || event.key === 'ArrowRight');

      if (!isDesktopSwitchShortcut) {
        return;
      }

      if (hasMachineControl) {
        const activeElement = document.activeElement;

        const isIframeFocused = activeElement?.tagName === 'IFRAME';

        const isInsideIframe = activeElement?.closest('iframe') !== null;

        if (isIframeFocused || isInsideIframe) {
          return;
        }
      }

      event.preventDefault();
      event.stopPropagation();

      if (viewerRef.current && document.fullscreenElement) {
        viewerRef.current.focus();
      }

      const { switchToNext, switchToPrevious } = handlersRef.current;

      if (event.key === 'ArrowLeft') {
        switchToPrevious();
      } else if (event.key === 'ArrowRight') {
        switchToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });

    const handleFocusIn = (event: FocusEvent) => {
      if (!hasMachineControl && isFullscreen && viewerRef.current) {
        const target = event.target as HTMLElement;
        if (target.tagName === 'IFRAME' || target.closest('iframe')) {
          viewerRef.current.focus();
        }
      }
    };

    window.addEventListener('focusin', handleFocusIn, { capture: true });

    const focusGuardInterval = window.setInterval(() => {
      if (!hasMachineControl && isFullscreen && viewerRef.current) {
        const activeElement = document.activeElement;
        if (activeElement?.tagName === 'IFRAME' || activeElement?.closest('iframe')) {
          viewerRef.current.focus();
        }
      }
    }, 5000);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      window.removeEventListener('focusin', handleFocusIn, { capture: true });
      window.clearInterval(focusGuardInterval);
    };
  }, [enabled, isFullscreen, canSwitch, hasMachineControl, viewerRef]);
};
